"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const destinations = [{
  country: "Faster Installations",
  description: "Our panel masonry system allows you to complete high-end outdoor kitchens and water features in days, not weeks, with significantly less labor.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/clifrock_fast_install.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/clifrock_fast_install.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/clifrock_fast_install.png-wvc-sizes",
    alt: "Contractors installing ClifRock panels"
  },
  link: "/WhyClifRockPage",
  features: ["NEW", "Labor Efficient"]
}, {
  country: "Premium Hardscapes",
  description: "Deliver the look of natural stone with engineered durability. Expand your portfolio with luxury fire pits, waterfalls, and outdoor living spaces.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/clifrock_premium_hardscape.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/clifrock_premium_hardscape.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/clifrock_premium_hardscape.png-wvc-sizes",
    alt: "Luxury outdoor living project"
  },
  link: "/WhyClifRockPage",
  features: ["High Margin", "Durable"]
}, {
  country: "Installer Support",
  description: "Join our network to access professional training, sales tools, and marketing resources designed to help your construction business scale rapidly.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/clifrock_training_support.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/clifrock_training_support.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686383/img/clifrock_training_support.png-wvc-sizes",
    alt: "ClifRock training session for contractors"
  },
  link: "/InstallerOpportunityPage",
  features: ["Training", "Marketing Tools"]
}];
export default function ContractorGrowthOpportunities() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="256025" id="contractor-growth-opportunities" className="bg-muted text-foreground py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-12 md:mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-4">
            Contractor Growth Opportunities
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default">
            Scale your business with the ClifRock panel masonry system. Build faster, 
            reduce labor costs, and deliver premium outdoor living projects.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 transition-all duration-1000 ease-out delay-200 ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {destinations.map((dest, i) => <div key={i} data-index={i} className="flex">
              <Card className="flex flex-col w-full overflow-hidden border-border shadow-md hover:shadow-xl transition-all duration-300 group bg-card rounded-lg">
                <div className="relative w-full h-56 overflow-hidden bg-muted">
                  <img src={dest.image.src} data-wvc-srcset={dest.image.srcset} data-wvc-sizes={dest.image.sizes} alt={dest.image.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-60" />
                  <div className="absolute bottom-4 left-4 right-4 flex gap-2 flex-wrap">
                    {dest.features.map((feature, idx) => <span key={idx} className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold backdrop-blur-sm shadow-sm ${feature === 'NEW' ? 'bg-primary text-primary-foreground' : 'bg-background/90 text-foreground'}`}>
                        {feature}
                      </span>)}
                  </div>
                </div>
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-2 mb-2 text-primary">
                    <MapPin className="w-5 h-5" />
                    <CardTitle className="text-2xl font-default text-foreground">
                      {dest.country}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="flex-grow pb-6">
                  <CardDescription className="text-base text-muted-foreground font-default leading-relaxed">
                    {dest.description}
                  </CardDescription>
                </CardContent>
                <CardFooter className="pt-0 mt-auto">
                  <Button asChild variant="secondary" className="w-full group/btn bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-lg">
                    <Link to={dest.link}>
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover/btn:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}